import { canPurchaseProduct, getProductSaleStatus, hasProductExposure, showExposurePrice } from "../../../../Component/tool/productExposure";
import type { LiveCouponAccess } from "../useLiveCouponProducts";
import { hasProductWaiting, isProductWaiting } from '../RestockWaitPanel';
import WaitBellIcon from '../WaitBellIcon';
import ProductImageViewer from "./ProductImageViewer";
import UserModalHeader from '../../../../Component/UI/UserModalHeader';
/* eslint-disable react/jsx-pascal-case */
import ContentState from "../../../../Component/UI/ContentState";
import styled, { keyframes } from "styled-components";
import {  RowView, CenterView } from "../../../../Component/style/common_style";
import { useCallback, useEffect, useRef, useState } from "react";
import Buy_Complete_Modal from "./Buy_Complete_Modal";
import { amountFormat } from "../../../../Component/tool/Tools";
import Buy_Modal from "./Buy_Modal";
import type { InventoryUpdate } from "../inventory";
import Product_Intro_Modal from "./Product_Intro_Modal";
import axios, { AxiosError } from "axios";
import url from "../../../../Component/server";
import { mediaUrl } from "../../../../Component/tool/mediaUrl";
import ProductThumbnail from '../../../../Component/UI/ProductThumbnail';

interface Item_List_Modal_Props {
  couponAccess?: LiveCouponAccess;
  visible: boolean;
  handleModal: React.Dispatch<React.SetStateAction<boolean>>;
  seq: number;
  isAuthenticated: boolean;
  onLoginRequired: () => void;
  onPurchaseComplete?: () => void;
  onOrderConfirmed?: () => void;
  inventoryUpdate?: InventoryUpdate;
  inventorySyncVersion?: number;
  productListSyncVersion?: number;
  sellerId?: string | number;
  canWait?: boolean;
  waitingGroups?: string[];
  onWaitJoined?: (group: string) => void;
}

interface BuyBtnProps {
  $count: number;
}

interface props_data_type {
  seq: number;
  live_seq: number;
  name: string;
  price: number;
  option_check: string;
  init_cnt: number;
  current_cnt: number;
  product_status: string;
  exposure_active?: boolean | null;
  exposure_price_mode?: 'visible' | 'seller' | 'hidden';
  product_num: number | null;
  unique_option1: string[];
  unique_option2: string[];
  thumbnail_img?: string;
}

const Item_List_Modal = (props: Item_List_Modal_Props) => {
  const { visible, handleModal } = props;
  const [BuyModal, setBuyModal] = useState(false);
  const [zoomItem, setZoomItem] = useState<props_data_type | null>(null);
  const [IntroModal, setIntroModal] = useState(false);
  const [data, set_Data] = useState<props_data_type[]>([]);
  const [hideSoldOut, setHideSoldOut] = useState(false);
  const visibleProducts = data.filter(item =>
    !hasProductExposure(item) && ['판매', '품절'].includes(getProductSaleStatus(item.product_status) || '')
    && (!hideSoldOut || (item.current_cnt > 0 && !['품절', '품절노출'].includes(item.product_status))));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestRef = useRef(0);
  const loadedSeqRef = useRef<number | null>(null);
  const inventoryDuringRequest = useRef(new Map<number, number>());
  const hasLoaded = loadedSeqRef.current === props.seq;
  const [selectedItem, setSelectedItem] = useState<props_data_type | null>(
    null
  ); // 선택된 아이템 상태 추가

  const openIntro = (item: props_data_type) => {
    setSelectedItem(item); // 선택된 아이템 저장
    setIntroModal(true); // 상품소개 모달 열기
  };

  const openBuy = (item: props_data_type) => {
    if (hasProductExposure(item) && !showExposurePrice(item.exposure_price_mode)) return;
    if (props.couponAccess && !props.couponAccess.canPurchase(item.seq)) return;
    const soldOut = item.current_cnt <= 0 || ['품절', '품절노출'].includes(item.product_status);
    if ((!soldOut && !canPurchaseProduct(item)) || (soldOut && (!props.canWait || isProductWaiting(props.waitingGroups || [], item)))) return;
    if (!props.isAuthenticated) { props.onLoginRequired(); return; }
    setSelectedItem(item); // 선택된 아이템 저장
    setIntroModal(false);
    setBuyModal(true); // 바로 구매 모달 열기
  };

  useEffect(() => {
    if (selectedItem && hasProductExposure(selectedItem) && !showExposurePrice(selectedItem.exposure_price_mode)) {
      setBuyModal(false);
      setIntroModal(false);
    }
  }, [selectedItem]);

  const load_productList = useCallback(async () => {
    const requestId = ++requestRef.current;
    inventoryDuringRequest.current.clear();
    // Keep existing rows mounted during refreshes so scrolling and purchase input survive.
    setLoading(loadedSeqRef.current !== props.seq);
    setError('');
    try {
      const res = await axios.get(url.Communication_url.product_list, {
        params: { live_seq: props.seq },
        timeout: 10000,
      });
      if (requestId !== requestRef.current) return;
      if (!Array.isArray(res.data)) throw new Error('Invalid product list');
      // A slower HTTP snapshot must not overwrite newer socket inventory updates.
      const products: props_data_type[] = res.data.map(item => ({
        ...item,
        current_cnt: inventoryDuringRequest.current.get(item.seq) ?? item.current_cnt,
      }));
      loadedSeqRef.current = props.seq;
      set_Data(products);
      setSelectedItem(previous => previous && (products.find(item => item.seq === previous.seq)
        ?? { ...previous, product_status: '대기', current_cnt: 0 }));
    } catch {
      if (requestId === requestRef.current) setError('상품 목록을 불러오지 못했습니다. 다시 시도해주세요.');
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [props.seq]);

  useEffect(() => {
    if (!visible) return;
    void load_productList();
    const resume = () => {
      if (document.visibilityState === 'visible') void load_productList();
    };
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('online', resume);
    return () => {
      requestRef.current += 1;
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('online', resume);
    };
  }, [visible, load_productList, props.inventorySyncVersion, props.productListSyncVersion]);

  useEffect(() => {
    const inventory = props.inventoryUpdate;
    if (!inventory) return;
    inventoryDuringRequest.current.set(inventory.productSeq, inventory.currentCnt);
    set_Data(previous => previous.map(item => item.seq === inventory.productSeq
      ? { ...item, current_cnt: inventory.currentCnt } : item));
    setSelectedItem(previous => previous?.seq === inventory.productSeq
      ? { ...previous, current_cnt: inventory.currentCnt } : previous);
  }, [props.inventoryUpdate]);

  const closeModal = () => {
    handleModal(false);
  };

  return (
    <BackgroundArea $visible={visible} $isOpen={visible} translate="no"
      onClick={event => { if (event.target === event.currentTarget) closeModal(); }}>
      <ModalBox $isOpen={visible} role="dialog" aria-modal="true" aria-label="상품목록" onClick={(e: any) => e.stopPropagation()}>
        {visible && zoomItem?.thumbnail_img && <ProductImageViewer images={[mediaUrl(zoomItem.thumbnail_img)]} name={zoomItem.name} onClose={() => setZoomItem(null)} />}
        <UserModalHeader title={hasLoaded ? `상품목록 (${visibleProducts.length})` : "상품목록"} closeLabel="상품목록 닫기" onClose={closeModal} />
        <FilterBar>
          <SoldOutToggle type="button" role="switch" aria-checked={hideSoldOut}
            onClick={() => setHideSoldOut(previous => !previous)}>
            품절 숨기기
            <ToggleTrack aria-hidden="true" />
          </SoldOutToggle>
        </FilterBar>
        <Content_Box>
          {loading && <ContentState compact kind="loading" icon="bag" title="상품 목록을 불러오고 있습니다." />}
          {hasLoaded && visibleProducts.length > 0 &&
            visibleProducts.map(item => {
              const soldOut = item.current_cnt <= 0 || ['품절', '품절노출'].includes(item.product_status);
              const waiting = isProductWaiting(props.waitingGroups || [], item);
              const anyWaiting = hasProductWaiting(props.waitingGroups || [], item);
              return (
                <div key={item.seq} style={{ width: "100%" }}>
                    <Item_Box>
                      <Item_Thumb_Wrapper as="button" type="button" aria-label={`${item.name} 이미지 확대 보기`}
                        disabled={!item.thumbnail_img} onClick={() => setZoomItem(item)} style={{ cursor: 'zoom-in', padding: 0, border: 0 }}>
                        <Item_ThumbImg
                          src={mediaUrl(item.thumbnail_img)}
                          alt={item.name}
                        />
                      </Item_Thumb_Wrapper>

                      <Item_Mid_Box onClick={() => openIntro(item)} style={{ cursor: "pointer" }}>
                        <Item_Name>{item.name}</Item_Name>
                        <Item_Price>{amountFormat(props.couponAccess?.priceFor(item.seq, item.price) ?? item.price)}원</Item_Price>
                      </Item_Mid_Box>

                      {soldOut && !(props.canWait && (!props.couponAccess || props.couponAccess.canPurchase(item.seq))) && <SoldOutStatus role="status">품절</SoldOutStatus>}
                      {(!props.couponAccess || props.couponAccess.canPurchase(item.seq)) && <Buy_Btn
                        data-waiting={soldOut && anyWaiting || undefined}
                        as="button"
                        disabled={(item.product_status === '대기' && !soldOut) || (soldOut && (!props.canWait || waiting))}
                        $count={(item.product_status === '대기' && !soldOut) || (soldOut && !props.canWait) ? 0 : Math.max(1, item.current_cnt)}
                        onClick={() => openBuy(item)}
                      >
                        {soldOut && props.canWait && <WaitBellIcon />}
                        {soldOut ? props.canWait ? anyWaiting ? '대기 중' : '대기' : '품절' : '구매'}
                      </Buy_Btn>}
                    </Item_Box>
                </div>
              );
            })}
          {hasLoaded && visibleProducts.length === 0 && <ContentState compact icon="bag"
            title={!hideSoldOut ? "표시할 상품이 없습니다." : "구매 가능한 상품이 없습니다."}
            description={!hideSoldOut ? "판매 상품이 등록되면 이곳에서 확인할 수 있어요." : "품절 숨기기를 끄면 품절된 상품도 볼 수 있어요."} />}
          {error && <ContentState compact kind="error" icon="bag" title={error} action={{ label: "다시 시도", onClick: load_productList }} />}
          <br />
          <br />
          <br />
          <br />
        </Content_Box>
      </ModalBox>
      <Buy_Modal
        visible={BuyModal}
        handleModal={setBuyModal}
        couponAccess={props.couponAccess}
        item={selectedItem}
        seq={props.seq}
        sellerId={props.sellerId}
        imageUrl={selectedItem?.thumbnail_img ? mediaUrl(selectedItem.thumbnail_img) : undefined}
        load_productList={load_productList}
        canWait={props.canWait}
        waitingGroups={props.waitingGroups}
        onWaitJoined={props.onWaitJoined}
        type="item_list"
        onPurchaseComplete={props.onPurchaseComplete}
        onOrderConfirmed={props.onOrderConfirmed}
        inventoryUpdate={props.inventoryUpdate}
        inventorySyncVersion={props.inventorySyncVersion}
      />
      <Product_Intro_Modal
        visible={IntroModal}
        handleModal={setIntroModal}
        refreshVersion={(props.productListSyncVersion ?? 0) + (props.inventorySyncVersion ?? 0)}
        couponAccess={props.couponAccess}
        item={selectedItem}
        isWaiting={Boolean(selectedItem && (selectedItem.current_cnt <= 0 || ['품절', '품절노출'].includes(selectedItem.product_status)) && isProductWaiting(props.waitingGroups || [], selectedItem))}
        onBuyClick={() => {
          if (selectedItem) openBuy(selectedItem);
        }}
      />
      {/* item 전달 */}
    </BackgroundArea>
  );
};

export default Item_List_Modal;

const SoldOutStatus = styled.span`
  flex: none; margin-right: 8px; color: var(--user-text-muted); font-size: 13px; font-weight: 600; white-space: nowrap;
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideDown = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;
const BackgroundArea = styled.div<{ $visible: boolean; $isOpen: boolean }>`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  background-color: #00000085;
  position: absolute;
  display: ${(props) => (props.$visible ? "flex" : "none")};
  justify-content: center;
  align-items: center;
  transition: opacity 0.5s ease;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  z-index: 99;
`;

const ModalBox = styled.div<{ $isOpen: boolean }>`
  box-sizing: border-box;
  width: 100%;
  max-width: 500px;
  height: 55%;
  background-color: var(--user-surface);
  border-top-right-radius: 16px;
  border-top-left-radius: 16px;
  overflow: hidden;
  position: absolute;
  bottom: 0px;
  animation: ${(props) => (props.$isOpen ? slideUp : slideDown)} 0.5s ease;
  color: var(--user-text);
  display: flex;
  flex-direction: column;
`;

const FilterBar = styled.div`
  display: flex;
  justify-content: flex-end;
  flex: none;
  padding: 4px 16px 0;
`;

const ToggleTrack = styled.span`
  width: 36px;
  height: 22px;
  border-radius: 999px;
  background: var(--user-border);
  position: relative;
  flex: none;
  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: 3px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: white;
  }
`;

const SoldOutToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--user-text);
  font: inherit;
  font-size: 14px;
  cursor: pointer;
  &[aria-checked='true'] ${ToggleTrack} {
    background: var(--user-accent);
    &::after { transform: translateX(14px); }
  }
  &:focus-visible { outline: 2px solid var(--user-accent); outline-offset: 3px; border-radius: 4px; }
`;

const Content_Box = styled.div`
  min-height: 0;
  width: 100%;
  flex: 1;
  overflow-y: auto;
  padding: 0px 16px;
  display: flex;
  position: relative;
  align-items: flex-start;
  box-sizing: border-box;
  flex-direction: column;
  font-size: 16px;

  /* 스크롤바 없애기 */
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Item_Box = styled.div`
  width: 100%;
  height: 90px;
  box-sizing: border-box;
  margin-top: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background-color: var(--user-surface);
`;

const Item_Thumb_Wrapper = styled.div`
  position: relative;
  width: 70px;
  height: 70px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
`;

const Item_ThumbImg = styled(ProductThumbnail)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;


const Item_Mid_Box = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  padding: 0 12px;
  box-sizing: border-box;
`;

const Item_Name = styled.div`
  font-size: 15px;
  color: var(--user-text);
  font-family: var(--font-Pretendard-Bold);
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Item_Price = styled.div`
  font-size: 16px;
  color: var(--user-error);
  font-family: var(--font-Pretendard-Bold);
`;

const Buy_Btn = styled.div<BuyBtnProps>`  gap: 4px;
  &[data-waiting="true"] { background: var(--user-surface-muted); color: var(--user-text-muted); }

  background: ${(props) =>
    props.$count === 0
      ? "var(--user-surface-raised)"
      : "linear-gradient(135deg, #7955F0, #3209B9)"};
  color: ${(props) => (props.$count === 0 ? "var(--user-text-muted)" : "white")};
  width: 80px;
  height: 50px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-family: var(--font-Pretendard-Bold);
  border-width: 0px;
  cursor: pointer;
  flex-shrink: 0;
  &:focus-visible { outline: 2px solid var(--user-accent); outline-offset: 2px; }
  &:disabled {
    cursor: not-allowed;
    background-color: var(--user-surface-raised);
    color: var(--user-text-muted);
    &:active {
      opacity: 1;
    }
  }
  &:active {
    opacity: 0.8;
  }
`;
